/**
 * Bodybound Theme JavaScript
 * Premium wellness e-commerce theme
 */

(function() {
  'use strict';

  // ========================================
  // Utility Functions
  // ========================================

  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [...context.querySelectorAll(selector)];

  const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), delay);
    };
  };

  // ========================================
  // Mobile Menu
  // ========================================

  const initMobileMenu = () => {
    const menuToggle = $('[data-menu-toggle]');
    const menuClose = $('[data-menu-close]');
    const mobileMenu = $('[data-mobile-menu]');

    if (!menuToggle || !mobileMenu) return;

    const openMenu = () => {
      mobileMenu.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const closeMenu = () => {
      mobileMenu.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    menuToggle.addEventListener('click', openMenu);
    menuClose?.addEventListener('click', closeMenu);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && mobileMenu.classList.contains('is-open')) {
        closeMenu();
      }
    });
  };

  // ========================================
  // Cart Drawer
  // ========================================

  const initCartDrawer = () => {
    const cartToggle = $('[data-cart-toggle]');
    const cartCloseButtons = $$('[data-cart-close]');
    const cartDrawer = $('[data-cart-drawer]');
    const cartOverlay = $('[data-cart-overlay]');

    if (!cartToggle || !cartDrawer) return;

    const openCart = () => {
      cartDrawer.classList.add('is-open');
      cartOverlay?.classList.add('is-visible');
      cartDrawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const closeCart = () => {
      cartDrawer.classList.remove('is-open');
      cartOverlay?.classList.remove('is-visible');
      cartDrawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    cartToggle.addEventListener('click', () => {
      if (cartDrawer.classList.contains('is-open')) {
        closeCart();
      } else {
        openCart();
      }
    });
    cartCloseButtons.forEach(btn => btn.addEventListener('click', closeCart));
    cartOverlay?.addEventListener('click', closeCart);

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && cartDrawer.classList.contains('is-open')) {
        closeCart();
      }
    });

    // Expose for external use
    window.openCartDrawer = openCart;
    window.closeCartDrawer = closeCart;
    window.refreshCartDrawer = refreshCartDrawer;
  };

  // ========================================
  // Product Gallery
  // ========================================

  const initProductGallery = () => {
    const mainImage = $('[data-main-image]');
    const thumbnails = $$('[data-thumbnail]');

    if (!mainImage || !thumbnails.length) return;

    thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', () => {
        const imageUrl = thumbnail.dataset.imageUrl;

        // Update main image
        mainImage.src = imageUrl;

        // Update active state
        thumbnails.forEach(t => t.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
      });
    });
  };

  // ========================================
  // Quantity Selector
  // ========================================

  const initQuantitySelector = () => {
    const selectors = $$('[data-quantity-selector]');

    selectors.forEach(selector => {
      const minusBtn = $('[data-quantity-minus]', selector);
      const plusBtn = $('[data-quantity-plus]', selector);
      const input = $('[data-quantity-input]', selector);

      if (!input) return;

      const updateValue = (delta) => {
        const currentValue = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 99;
        const newValue = Math.min(Math.max(currentValue + delta, min), max);
        input.value = newValue;

        // Dispatch change event
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      minusBtn?.addEventListener('click', () => updateValue(-1));
      plusBtn?.addEventListener('click', () => updateValue(1));

      input.addEventListener('change', () => {
        const min = parseInt(input.min, 10) || 1;
        const max = parseInt(input.max, 10) || 99;
        let value = parseInt(input.value, 10) || min;
        input.value = Math.min(Math.max(value, min), max);
      });
    });
  };

  // ========================================
  // Product Variant Selector
  // ========================================

  const initVariantSelector = () => {
    const productForm = $('[data-product-form]');
    const productJson = $('[data-product-json]');

    if (!productForm || !productJson) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch (e) {
      console.error('Failed to parse product JSON:', e);
      return;
    }

    const optionInputs = $$('[data-option-input]', productForm);
    const variantIdInput = $('[data-variant-id]', productForm);
    const priceElement = $('[data-product-price]');
    const addToCartBtn = $('[data-add-to-cart]', productForm);

    const getSelectedOptions = () => {
      const options = [];
      const selectedInputs = $$('[data-option-input]:checked', productForm);
      selectedInputs.forEach(input => {
        options.push(input.value);
      });
      return options;
    };

    const findVariant = (options) => {
      return product.variants.find(variant => {
        return options.every((option, index) => {
          return variant.options[index] === option;
        });
      });
    };

    const updatePrice = (variant) => {
      if (!priceElement) return;

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        priceElement.innerHTML = `
          ${formatMoney(variant.price)}
          <span class="product__price-compare">${formatMoney(variant.compare_at_price)}</span>
        `;
      } else {
        priceElement.innerHTML = formatMoney(variant.price);
      }
    };

    const updateAddToCart = (variant) => {
      if (!addToCartBtn) return;

      if (variant && variant.available) {
        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Cart';
      } else if (variant) {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Sold Out';
      } else {
        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Unavailable';
      }
    };

    const onOptionChange = () => {
      const options = getSelectedOptions();
      const variant = findVariant(options);

      if (variant) {
        variantIdInput.value = variant.id;
        updatePrice(variant);
        updateAddToCart(variant);

        // Update URL
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      } else {
        updateAddToCart(null);
      }
    };

    optionInputs.forEach(input => {
      input.addEventListener('change', onOptionChange);
    });
  };

  // ========================================
  // Format Money Helper
  // ========================================

  const formatMoney = (cents) => {
    const amount = (cents / 100).toFixed(2);
    return `USD ${amount}`;
  };

  // ========================================
  // Accordion
  // ========================================

  const initAccordion = () => {
    const accordions = $$('[data-accordion]');

    accordions.forEach(accordion => {
      const triggers = $$('[data-accordion-trigger]', accordion);

      triggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('[data-accordion-item]');
          const isOpen = item.classList.contains('is-open');

          // Close all items
          $$('[data-accordion-item]', accordion).forEach(i => {
            i.classList.remove('is-open');
            $('[data-accordion-trigger]', i)?.setAttribute('aria-expanded', 'false');
          });

          // Open clicked item if it was closed
          if (!isOpen) {
            item.classList.add('is-open');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  };

  // ========================================
  // Add to Cart
  // ========================================

  const initAddToCart = () => {
    const productForms = $$('[data-product-form]');

    productForms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const submitBtn = $('[data-add-to-cart]', form);
        const originalText = submitBtn.textContent;

        // Disable button and show loading state
        submitBtn.disabled = true;
        submitBtn.textContent = 'Adding...';

        const formData = new FormData(form);
        // Request the cart-drawer section inline so it is rendered
        // server-side in the same request context as the cart mutation.
        // This avoids a separate /?sections=cart-drawer round-trip, which
        // does not share the cart session on Shopify preview URLs.
        formData.append('sections', 'cart-drawer');
        formData.append('sections_url', window.location.pathname);

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (!response.ok) throw new Error('Add to cart failed');

          const result = await response.json();

          // Update cart count
          updateCartCount();

          // Show success state
          submitBtn.textContent = 'Added!';

          // Patch drawer HTML from the inline section render
          const sectionHTML = result.sections?.['cart-drawer'];
          if (sectionHTML) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHTML, 'text/html');
            const freshContent = doc.querySelector('[data-cart-content]');
            const freshFooter  = doc.querySelector('[data-cart-footer]');
            const currentContent = document.querySelector('[data-cart-content]');
            const currentFooter  = document.querySelector('[data-cart-footer]');
            if (freshContent && currentContent) currentContent.innerHTML = freshContent.innerHTML;
            if (freshFooter && currentFooter) {
              currentFooter.innerHTML = freshFooter.innerHTML;
              currentFooter.style.display = freshFooter.style.display;
            }
          }

          // Wait for BOTH the minimum UX delay AND renderCart() before opening the
          // drawer so prices are correct on first appearance.
          if (window.openCartDrawer) {
            const minDelay = new Promise(r => setTimeout(r, 500));
            const cartRender = fetch('/cart.js').then(async r => {
              if (r.ok && window.renderCart) window.renderCart(await r.json());
            }).catch(() => {});
            await Promise.all([minDelay, cartRender]);
            window.openCartDrawer();
            // Safety net: if /cart.js was slow or failed above, re-run now that
            // the drawer is visible. No-op if renderCart already ran correctly.
            fetch('/cart.js').then(async r => {
              if (r.ok && window.renderCart) window.renderCart(await r.json());
            }).catch(() => {});
          }

          // Reset button
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1500);

        } catch (error) {
          console.error('Error adding to cart:', error);
          submitBtn.textContent = 'Error';
          setTimeout(() => {
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 2000);
        }
      });
    });
  };

  // ========================================
  // Update Cart Count
  // ========================================

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Update cart toggle buttons with has-items class and aria-label
      const cartToggles = $$('[data-cart-toggle]');
      cartToggles.forEach(el => {
        if (cart.item_count > 0) {
          el.classList.add('has-items');
          const itemWord = cart.item_count === 1 ? 'item' : 'items';
          el.setAttribute('aria-label', `Cart, ${cart.item_count} ${itemWord}`);
        } else {
          el.classList.remove('has-items');
          el.setAttribute('aria-label', 'Cart');
        }
        el.dataset.cartItemCount = cart.item_count;
      });
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  };

  // ========================================
  // Refresh Cart Drawer
  // ========================================

  const refreshCartDrawer = async () => {
    try {
      const [sectionRes, cartRes] = await Promise.all([
        fetch('/?sections=cart-drawer'),
        fetch('/cart.js')
      ]);

      const cart = cartRes.ok ? await cartRes.json() : null;
      const data = await sectionRes.json();
      const html = data['cart-drawer'];

      if (html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const freshContent = doc.querySelector('[data-cart-content]');
        const freshFooter  = doc.querySelector('[data-cart-footer]');
        const currentContent = document.querySelector('[data-cart-content]');
        const currentFooter  = document.querySelector('[data-cart-footer]');

        // Only replace content when the section render has items or the cart
        // is genuinely empty. Preview URLs render sections with an empty cart
        // even when items exist, which would wipe the item rows.
        const sectionHasItems = freshContent?.querySelector('[data-cart-item]');
        const cartIsEmpty = !cart || cart.item_count === 0;
        if (freshContent && currentContent && (sectionHasItems || cartIsEmpty)) {
          currentContent.innerHTML = freshContent.innerHTML;
        }
        if (freshFooter && currentFooter) {
          currentFooter.innerHTML = freshFooter.innerHTML;
          currentFooter.style.display = freshFooter.style.display;
        }
      }

      // Call renderCart only when cart has items — renderCart() itself calls
      // refreshCartDrawer() when empty, so guarding here avoids an infinite loop.
      if (cart?.item_count > 0 && window.renderCart) {
        window.renderCart(cart);
      }
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  };

  // ========================================
  // Header Hide on Scroll (DISABLED - keep header always visible)
  // ========================================

  const initHeaderScroll = () => {
    // Header stays sticky, doesn't hide on scroll
    // Keeping function stub for potential future use
  };

  // ========================================
  // Initialize
  // ========================================

  const init = () => {
    initMobileMenu();
    initCartDrawer();
    initProductGallery();
    initQuantitySelector();
    initVariantSelector();
    initAccordion();
    initAddToCart();
    initHeaderScroll();
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
